"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"
import type {
  WizardState,
  Platform,
  SyncSection,
  ConnectorData,
  ConnectorApiResponse,
  AuthStatusResponse,
  SyncConfig,
} from "../types/edit-connector-wizard"
import { useToast } from "../context/ToastContext";
import { DEFAULT_PLATFORMS, DEFAULT_SYNC_SECTIONS, WIZARD_CONSTANTS } from "../constants/edit-connector-wizard"

export const useEditConnectorWizard = (tenantConnectorId?: string, tenantId?: string) => {
  const { getConnectorDetails, getAuthStatus, updateConnectorConfig, authoriseHubspot, authoriseSalesforce } = useAuth()

  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    completedSteps: [],
    totalSteps: WIZARD_CONSTANTS.TOTAL_STEPS,
  })

  // Data states
  const [connectorData, setConnectorData] = useState<ConnectorData | null>(null)
  const [platforms, setPlatforms] = useState<Platform[]>(DEFAULT_PLATFORMS)
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({})
  const [sourceFields, setSourceFields] = useState<string[]>([])
  const [targetFields, setTargetFields] = useState<string[]>([])
  const [syncRules, setSyncRules] = useState<SyncSection[]>(DEFAULT_SYNC_SECTIONS)

  // Loading states
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errorStatus, setErrorStatus] = useState<string | null>(null)
  const [successStatus, setSuccessStatus] = useState<string | null>(null)
  const navigate = useNavigate()
  const { error, success } = useToast();

  // Update sync configuration from API response
  const updateSyncConfigFromApi = useCallback((syncConfig?: SyncConfig) => {
    const defaultConfig: SyncConfig = {
      hs_to_sf: {
        create_enabled: true,
        update_enabled: true,
        delete_enabled: true,
        restore_enabled: true,
      },
      sf_to_hs: {
        create_enabled: true,
        update_enabled: true,
        delete_enabled: true,
        restore_enabled: true,
      },
    }

    const configToUse = syncConfig || defaultConfig
    setSyncRules((prevRules) =>
      prevRules.map((section) => ({
        ...section,
        rules: section.rules.map((rule) => {
          let enabled = rule.enabled

          // Map HubSpot to Salesforce rules
          if (rule.sourceIcon === "hubspot" && rule.targetIcon === "salesforce") {
            if (rule.id.includes("create")) {
              enabled = configToUse.hs_to_sf.create_enabled
            } else if (rule.id.includes("update")) {
              enabled = configToUse.hs_to_sf.update_enabled
            } else if (rule.id.includes("delete")) {
              enabled = configToUse.hs_to_sf.delete_enabled
            }
          }
          // Map Salesforce to HubSpot rules
          else if (rule.sourceIcon === "salesforce" && rule.targetIcon === "hubspot") {
            if (rule.id.includes("create")) {
              enabled = configToUse.sf_to_hs.create_enabled
            } else if (rule.id.includes("update")) {
              enabled = configToUse.sf_to_hs.update_enabled
            } else if (rule.id.includes("delete")) {
              enabled = configToUse.sf_to_hs.delete_enabled
            }
          }

          return { ...rule, enabled }
        }),
      })),
    )

    // Store in localStorage for persistence
    localStorage.setItem("syncConfiguration", JSON.stringify(configToUse))
  }, [])

  // Load connector data
  const loadConnectorData = useCallback(async () => {
    if (!tenantConnectorId || !tenantId) return

    setLoading(true)
    setErrorStatus(null)

    try {
      const tenant_connectorId = Number(tenantConnectorId)
      const parseTenantId = Number(tenantId)

      // Get connector details
      const connectorResponse: ConnectorApiResponse = await getConnectorDetails(tenant_connectorId)

      if (connectorResponse.status === 200 && connectorResponse.data) {
        const {
          connector,
          sourceFields: apiSourceFields,
          targetFields: apiTargetFields,
        } = connectorResponse.data

        // Update sync configuration
        localStorage.setItem("source_object",connector.source_object);
        localStorage.setItem("target_object",connector.target_object);
        updateSyncConfigFromApi(connector.syncConfig)

        setConnectorData(connector)
        setFieldMappings(connector.field_mappings)
        setSourceFields(apiSourceFields)
        setTargetFields(apiTargetFields)
      }

      // Get auth status
      const authResponse: AuthStatusResponse = await getAuthStatus({
        tanentConnectorId: parseTenantId,
        tanentId: tenant_connectorId,
      })

      if (authResponse.status === 200 && authResponse.data) {
        setPlatforms((prev) =>
          prev.map((platform) => ({
            ...platform,
            connected:
              platform.id === "hubspot"
                ? authResponse.data.hubspot_connection_status
                : authResponse.data.salesforce_connection_status,
          })),
        )
      }
    } catch (err: any) {
      console.error("Error loading connector data:", err)
      error(err.message || "Failed to load connector data")
      setErrorStatus(err.message || "Failed to load connector data")
    } finally {
      setLoading(false)
    }
  }, [tenantConnectorId, tenantId, getAuthStatus, getConnectorDetails, updateSyncConfigFromApi, error])

  // Load data on mount
  useEffect(() => {
    loadConnectorData()
  }, [loadConnectorData])

  // Navigation functions
  const nextStep = useCallback(() => {
    setWizardState((prev) => {
      const newCompletedSteps = [...prev.completedSteps]
      if (!newCompletedSteps.includes(prev.currentStep)) {
        newCompletedSteps.push(prev.currentStep)
      }

      return {
        ...prev,
        currentStep: Math.min(prev.currentStep + 1, prev.totalSteps),
        completedSteps: newCompletedSteps,
      }
    })
  }, [])

  const prevStep = useCallback(() => {
    setWizardState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }))
  }, [])

  const updateStepCompletion = useCallback((step: number, completed: boolean) => {
    setWizardState((prev) => {
      const newCompletedSteps = [...prev.completedSteps]
      const stepIndex = newCompletedSteps.indexOf(step)

      if (completed && stepIndex === -1) {
        newCompletedSteps.push(step)
      } else if (!completed && stepIndex !== -1) {
        newCompletedSteps.splice(stepIndex, 1)
      }

      return {
        ...prev,
        completedSteps: newCompletedSteps,
      }
    })
  }, [])

  // Platform connection
  const connectPlatform = useCallback(async (platformId: string, tanentId:number) => {
    setPlatforms((prev) => prev.map((p) => (p.id === platformId ? { ...p, connecting: true } : p)))
    // Update the token
    try {
      // Replace with actual API call
      platformId === 'hubspot' ? await authoriseHubspot() : await authoriseSalesforce(tanentId);
      setPlatforms((prev) => prev.map((p) => (p.id === platformId ? { ...p, connected: true, connecting: false } : p)))
    } catch (error) {
      setPlatforms((prev) => prev.map((p) => (p.id === platformId ? { ...p, connecting: false } : p)))
    }
  }, [])

  // Field mapping functions
  const updateFieldMapping = useCallback((sourceField: string, targetField: string) => {
    setFieldMappings((prev) => ({
      ...prev,
      [sourceField]: targetField,
    }))
    setErrorStatus(null)
    setSuccessStatus(null)
  }, [])

  const removeFieldMapping = useCallback((sourceField: string) => {
    setFieldMappings((prev) => {
      const newMappings = { ...prev }
      delete newMappings[sourceField]
      return newMappings
    })
  }, [])

  const addFieldMapping = useCallback(
    (sourceField: string, targetField: string) => {
      if (sourceField && targetField && !fieldMappings[sourceField]) {
        updateFieldMapping(sourceField, targetField)
        return true
      }
      return false
    },
    [fieldMappings, updateFieldMapping],
  )

  // Sync rules functions
  const toggleSyncRule = useCallback((sectionId: string, ruleId: string) => {
    setSyncRules((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              rules: section.rules.map((rule) => (rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule)),
            }
          : section,
      ),
    )
  }, [])

  const toggleSection = useCallback((sectionId: string) => {
    setSyncRules((prev) =>
      prev.map((section) => (section.id === sectionId ? { ...section, expanded: !section.expanded } : section)),
    )
  }, [])

  // Convert sync rules to API format
  const getSyncConfigForApi = useCallback((): SyncConfig => {
    const config: SyncConfig = {
      hs_to_sf: {
        create_enabled: false,
        update_enabled: false,
        delete_enabled: false,
        restore_enabled: false,
      },
      sf_to_hs: {
        create_enabled: false,
        update_enabled: false,
        delete_enabled: false,
        restore_enabled: false,
      },
    }

    syncRules.forEach((section) => {
      section.rules.forEach((rule) => {
        if (rule.sourceIcon === "hubspot" && rule.targetIcon === "salesforce") {
          if (rule.id.includes("create")) {
            config.hs_to_sf.create_enabled = rule.enabled
          } else if (rule.id.includes("update")) {
            config.hs_to_sf.update_enabled = rule.enabled
          } else if (rule.id.includes("delete")) {
            config.hs_to_sf.delete_enabled = rule.enabled
          }
        } else if (rule.sourceIcon === "salesforce" && rule.targetIcon === "hubspot") {
          if (rule.id.includes("create")) {
            config.sf_to_hs.create_enabled = rule.enabled
          } else if (rule.id.includes("update")) {
            config.sf_to_hs.update_enabled = rule.enabled
          } else if (rule.id.includes("delete")) {
            config.sf_to_hs.delete_enabled = rule.enabled
          }
        }
      })
    })

    return config
  }, [syncRules])

  // Save all changes
  const saveAllChanges = useCallback(async () => {
    if (!connectorData || !tenantConnectorId || !tenantId) return false

    setSaving(true)
    setErrorStatus(null)

    try {
      const syncConfig = getSyncConfigForApi()

      const updatePayload = {
        tenant_connector_id: Number.parseInt(tenantConnectorId),
        connectorId: connectorData.connector_id,
        tenant_id: tenantId,
        field_mappings: fieldMappings,
        sync_direction: connectorData.sync_direction,
        syncConfig,
      }
      console.log(updatePayload)
      // return true
      const response = await updateConnectorConfig(updatePayload)

      if (response.status === 200) {
        setSuccessStatus("Connector updated successfully!")
        success("Connector updated successfully!")
        navigate("/dashboard")
        return true
      } else {
        throw new Error(response.message || "Failed to update connector")
      }
    } catch (err: any) {
      console.error("Error saving changes:", err)
      setErrorStatus(err.message || "Failed to save changes")
      return false
    } finally {
      setSaving(false)
    }
  }, [connectorData, tenantConnectorId, tenantId, fieldMappings, updateConnectorConfig, getSyncConfigForApi, success,navigate])

  return {
    // State
    wizardState,
    platforms,
    fieldMappings,
    syncRules,
    sourceFields,
    targetFields,
    connectorData,
    loading,
    saving,
    errorStatus,
    successStatus,

    // Actions
    nextStep,
    prevStep,
    updateStepCompletion,
    connectPlatform,
    updateFieldMapping,
    removeFieldMapping,
    addFieldMapping,
    toggleSyncRule,
    toggleSection,
    saveAllChanges,
    loadConnectorData,
  }
}
